import { skipExportNodes, safePixelRatio, warnIfClamped } from './exportImage'

const A4_WIDTH_PT = 595.28
const A4_HEIGHT_PT = 841.89
const INNER_WIDTH_REF = 592.28

// Per-element placement (exportElementsAsPdf): real page margins, so images are
// inset rather than full-bleed, and a small gap separates stacked elements.
const MARGIN_PT = 36
const GAP_PT = 12
const CONTENT_WIDTH_PT = A4_WIDTH_PT - 2 * MARGIN_PT
const CONTENT_HEIGHT_PT = A4_HEIGHT_PT - 2 * MARGIN_PT

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}

export async function exportAsPdf(el: HTMLElement, filename: string): Promise<void> {
  const { toPng } = await import('html-to-image')
  const { jsPDF } = await import('jspdf')

  const pixelRatio = safePixelRatio(el.scrollWidth, el.scrollHeight)
  warnIfClamped('exportAsPdf', el.scrollWidth, el.scrollHeight, pixelRatio)
  const dataUrl = await toPng(el, {
    pixelRatio,
    cacheBust: true,
    filter: skipExportNodes,
  })
  const img = await loadImage(dataUrl)

  const contentWidth = img.naturalWidth
  const contentHeight = img.naturalHeight
  const pageHeight = (contentWidth / INNER_WIDTH_REF) * A4_HEIGHT_PT
  const imgWidth = A4_WIDTH_PT
  const imgHeight = (INNER_WIDTH_REF / contentWidth) * contentHeight

  const pdf = new jsPDF('p', 'pt', 'a4')

  if (contentHeight <= pageHeight) {
    pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight)
  } else {
    let leftHeight = contentHeight
    let position = 0
    while (leftHeight > 0) {
      pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight)
      leftHeight -= pageHeight
      position -= A4_HEIGHT_PT
      if (leftHeight > 0) {
        pdf.addPage()
      }
    }
  }

  pdf.save(filename)
}

/**
 * Page-break-aware, per-element PDF placement. Each element is rasterized independently
 * and placed as a whole image — never sliced across a page boundary. Used for page-level
 * exports (Home/CollectionItem dashboards) so each pinned chart survives export intact,
 * unlike the whole-container rasterize-then-slice `exportAsPdf` above, which cuts mid-chart
 * whenever a chart straddles an A4 page seam.
 *
 * Placement: a running `y` cursor tracks the next free spot on the current page (starting
 * at the top margin). For each element, scale it to the page content width (A4 width minus
 * left+right margins), preserving aspect ratio. If the scaled height exceeds a full content
 * page (taller than one page can ever hold), scale BOTH dimensions down uniformly (preserving
 * aspect ratio) so the height fits the content-page height — it gets its own page, scaled down,
 * rather than looping to slice it or squishing it to full width. If the (possibly capped) height
 * does not fit in the remaining space on the current page, start a new page and reset `y` to
 * the top margin. Then place the image at (leftMargin, y) and advance `y` by the placed
 * height plus a small gap.
 */
export async function exportElementsAsPdf(
  elements: HTMLElement[],
  filename: string,
): Promise<void> {
  const { toPng } = await import('html-to-image')
  const { jsPDF } = await import('jspdf')

  const pdf = new jsPDF('p', 'pt', 'a4')
  let y = MARGIN_PT

  for (const el of elements) {
    const pixelRatio = safePixelRatio(el.scrollWidth, el.scrollHeight)
    warnIfClamped('exportElementsAsPdf', el.scrollWidth, el.scrollHeight, pixelRatio)
    const dataUrl = await toPng(el, {
      pixelRatio,
      cacheBust: true,
      filter: skipExportNodes,
    })
    const img = await loadImage(dataUrl)

    const contentWidth = img.naturalWidth
    const contentHeight = img.naturalHeight
    let placedWidth = CONTENT_WIDTH_PT
    let placedHeight = (CONTENT_WIDTH_PT / contentWidth) * contentHeight

    // A single element taller than a full content page scales down UNIFORMLY (both dimensions,
    // preserving aspect ratio) to fit one page, instead of being sliced across pages or squished.
    if (placedHeight > CONTENT_HEIGHT_PT) {
      const scale = CONTENT_HEIGHT_PT / placedHeight
      placedHeight = CONTENT_HEIGHT_PT
      placedWidth = CONTENT_WIDTH_PT * scale
    }

    const remaining = A4_HEIGHT_PT - MARGIN_PT - y
    if (placedHeight > remaining) {
      pdf.addPage()
      y = MARGIN_PT
    }

    pdf.addImage(dataUrl, 'PNG', MARGIN_PT, y, placedWidth, placedHeight)
    y += placedHeight + GAP_PT
  }

  pdf.save(filename)
}
